// Session 55.ag · Payment success page — polls /api/payments/status/{sid}
// every 2s until the session flips to `paid`. Never claims success until
// Stripe confirms.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 15;   // 30s cap — beyond this we show a "still processing" state

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id") || "";
  const [status, setStatus] = useState({ status: "loading", payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!sid) { setErr("No session id."); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        setStatus(r.data);
        setAttempts(n);
        if (r.data.payment_status !== "paid" && n < MAX_ATTEMPTS) {
          setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Status check failed.");
      }
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sid]);

  const paid = status.payment_status === "paid";
  const stillPolling = !paid && attempts > 0 && attempts < MAX_ATTEMPTS;
  const timeout = !paid && attempts >= MAX_ATTEMPTS;

  return (
    <AppShell>
      <div className="m-page-pad" data-testid="payment-success-page" style={{maxWidth:640, textAlign:"center"}}>
        {paid && <>
          <div style={{fontSize:64, marginBottom:8}}>✓</div>
          <span className="m-eyebrow">Welcome</span>
          <h1 className="m-h1">You&apos;re <em>in</em></h1>
          <p className="m-muted" style={{marginTop:8}}>
            Payment confirmed. Your Founding Member benefits are active.
          </p>
          <div style={{marginTop:26}}>
            <Link to="/spaces" data-testid="payment-success-cta"
              style={{padding:"12px 26px", background:"#B8913A", color:"#FAF8F3", borderRadius:999, textDecoration:"none", fontSize:14}}>
              Browse spaces →
            </Link>
          </div>
        </>}
        {stillPolling && <>
          <div style={{fontSize:48, marginBottom:8, color:"#B8913A"}}>◐</div>
          <span className="m-eyebrow">Processing</span>
          <h1 className="m-h1">Confirming <em>payment</em>…</h1>
          <p className="m-muted" style={{marginTop:8}}>Stripe is finalizing your charge. This usually takes a couple of seconds.</p>
        </>}
        {timeout && <>
          <span className="m-eyebrow">Almost there</span>
          <h1 className="m-h1">Still <em>processing</em></h1>
          <p className="m-muted" style={{marginTop:8}}>
            Your payment is taking a little longer than usual to confirm. You&apos;ll receive a receipt by email — no need to pay again.
          </p>
        </>}
        {err && <div style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginTop:20, fontSize:13}}>{err}</div>}
      </div>
    </AppShell>
  );
}
